import styled from '@emotion/styled';
import { DISCONNECT_REASON } from '@racemap/sdk/schema/trackers';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { isAccessible } from '@racemap/utilities/functions/trackers';
import { Popover } from 'antd';
import type { TableCellProps } from 'react-virtualized';
import { useCurrentUser } from '../../../../lib/customHooks';
import type { ManagedTracker } from '../../../../store/trackers/trackers_reducers';
import { TimestampAndIcon } from '../../../BasicComponents/Timestamp/TimestampAndIcon';
import { Columns } from '../../../DataTable';
import { IconArrowDoubleRight, IconFailCircle, IconNetwork, IconTowerCell } from '../../../Icon';
import {
  OnlineFilters,
  getOnlineStateRank,
  getOnlineStateValue,
} from '../../Filter/FilterTreeFunctions';

interface CellProps extends TableCellProps {
  cellData?: ManagedTracker['state'];
  rowData: ManagedTracker;
}

export const OnlineColumn = () => {
  const currentUser = useCurrentUser();

  const DisconnectReasonLabels: Record<DISCONNECT_REASON, string> = {
    [DISCONNECT_REASON.NONE]:
      'Offline: Either the device or Racemap closed the socket. Racemap auto-closes the socket after 5 minutes without receiving a message from the device.',
    [DISCONNECT_REASON.SYNC_WITH_R_R]: 'Sync with Race|Result caused disconnection.',
    [DISCONNECT_REASON.WEBSOCKET_CLOSED]: 'Websocket closed',
    [DISCONNECT_REASON.SOCKET_CLOSED]: 'Socket closed',
    [DISCONNECT_REASON.SOCKET_ERROR]: 'Socket error',
    [DISCONNECT_REASON.SOCKET_TIMEOUT]: 'Socket timeout',
    [DISCONNECT_REASON.POWER_OFF_BUFFERED]: 'Power Off Package (Buffered)',
    [DISCONNECT_REASON.POWER_OFF]: 'Power Off Package',
    [DISCONNECT_REASON.KILL_SIGNAL]: 'Service Maintenance',
    [DISCONNECT_REASON.NO_RECENT_LOCATION_UPDATE]:
      'No recent location update (older than 5 minutes).',
  };

  const cellRenderer = ({ rowData }: CellProps) => {
    if (rowData == null || !isAccessible(rowData, currentUser)) return null;
    const state = getOnlineStateValue(rowData.state);

    switch (state) {
      case OnlineFilters.Connected:
        return (
          <TimestampAndIcon
            icon={
              <IconArrowDoubleRight
                color={RacemapColors.DarkGreen}
                title="Online: The device opened a socket to Racemap."
              />
            }
            time={rowData.state.lastConnectedAt}
          />
        );
      case OnlineFilters.Data:
        return <IconNetwork color={RacemapColors.DarkYellow} title="connected with internet" />;
      case OnlineFilters.Registered:
        return (
          <IconTowerCell color={RacemapColors.DarkYellow} title="registered in mobile network" />
        );
      default:
        return (
          <TimestampAndIcon
            icon={
              <Popover
                content={
                  <ContentContainer>
                    {DisconnectReasonLabels[rowData.state.disconnectReason]}
                  </ContentContainer>
                }
                placement="top"
              >
                <IconFailCircle color={RacemapColors.BaseRed} />
              </Popover>
            }
            time={rowData.state.lastDisconnectedAt}
          />
        );
    }
  };

  return Columns.sortingColumn({
    style: { justifyContent: 'flex-end' },
    dataKey: 'state.online',
    label: 'CON',
    headerTitle: 'Duration since the socket was opened or closed.',
    cellRenderer,
    minWidth: 80,
    sortingMethod: (_cA, _cB, rowDataA: ManagedTracker, rowDataB: ManagedTracker) => {
      const rankA = getOnlineStateRank(rowDataA.state);
      const rankB = getOnlineStateRank(rowDataB.state);

      // Online before Offline
      if (rankA !== rankB) {
        return rankB - rankA;
      }

      // Online group, sort by lastConnectedAt
      if (rankA > 0) {
        const tA = rowDataA.state.lastConnectedAt?.getTime() ?? 0;
        const tB = rowDataB.state.lastConnectedAt?.getTime() ?? 0;
        return tB - tA;
      }
      // Offline group, sort by lastDisconnectedA
      const tA = rowDataA.state.lastDisconnectedAt?.getTime() ?? 0;
      const tB = rowDataB.state.lastDisconnectedAt?.getTime() ?? 0;
      return tB - tA;
    },
  });
};

const ContentContainer = styled.div`
  max-width: 200px;
`;
